import React, { useRef, useEffect, useState } from 'react';
import Chart from 'chart.js/auto';
import { fetchHeatPumps } from '../../services/heatPumpService';
import { supabaseClient } from '../../services/supabase';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';

const formatSupabaseError = (err: unknown) => {
  if (!err || typeof err !== 'object') return 'Unknown error';
  const anyErr = err as { message?: string; details?: string; hint?: string; code?: string };
  const parts = [anyErr.message, anyErr.details, anyErr.hint, anyErr.code].filter(Boolean);
  return parts.length ? parts.join(' | ') : 'Unknown error';
};

interface HeatPumpPerCapitaChartProps {
  location: string;
  geofips: number;
  onDataLoaded?: (rows: { year: number; location: string; value: number }[]) => void;
}

export const HeatPumpPerCapitaChart: React.FC<HeatPumpPerCapitaChartProps> = ({ location, geofips, onDataLoaded }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPerCapitaData = async () => {
      if (!location || !geofips) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        // Fetch heat pump data for location and California, and population data
        const [locationData, californiaData, locationPop, californiaPop] = await Promise.all([
          fetchHeatPumps({ select: 'year, units_installed', geofips }),
          fetchHeatPumps({ select: 'year, units_installed', geofips: 6 }),
          supabaseClient
            .from('population')
            .select('year, population')
            .eq('geofips', geofips)
            .order('year', { ascending: true }),
          supabaseClient
            .from('population')
            .select('year, population')
            .eq('geofips', 6)
            .order('year', { ascending: true })
        ]);

        if (locationData.error) {
          console.error('Error fetching location heat pump data:', locationData.error);
          throw locationData.error;
        }
        if (californiaData.error) {
          console.error('Error fetching California heat pump data:', californiaData.error);
          throw californiaData.error;
        }
        if (locationPop.error) {
          console.error('Error fetching location population data:', locationPop.error);
          throw locationPop.error;
        }
        if (californiaPop.error) {
          console.error('Error fetching California population data:', californiaPop.error);
          throw californiaPop.error;
        }

        // Aggregate units by year for location
        const locationByYear = new Map<number, number>();
        locationData.data?.forEach(record => {
          const year = record.year;
          locationByYear.set(year, (locationByYear.get(year) || 0) + (record.units_installed || 0));
        });

        // Aggregate units by year for California
        const californiaByYear = new Map<number, number>();
        californiaData.data?.forEach(record => {
          const year = record.year;
          californiaByYear.set(year, (californiaByYear.get(year) || 0) + (record.units_installed || 0));
        });

        // Create population maps
        const locationPopMap = new Map<number, number>();
        locationPop.data?.forEach(record => {
          locationPopMap.set(record.year, record.population || 0);
        });

        const californiaPopMap = new Map<number, number>();
        californiaPop.data?.forEach(record => {
          californiaPopMap.set(record.year, record.population || 0);
        });

        // Find the most recent year with data for both heat pumps and population
        const currentYear = new Date().getFullYear();
        const locationYears = new Set(
          Array.from(locationByYear.keys()).filter(
            year => locationPopMap.has(year) && year <= currentYear
          )
        );
        const californiaYears = new Set(
          Array.from(californiaByYear.keys()).filter(
            year => californiaPopMap.has(year) && year <= currentYear
          )
        );
        const eligibleYears = Array.from(locationYears).filter(year => californiaYears.has(year));
        if (eligibleYears.length === 0) {
          setError('No heat pump and population data available for the same year.');
          return;
        }
        const latestYear = Math.max(...eligibleYears);
        // Calculate per 10,000 residents for latest year
        const locationUnits = locationByYear.get(latestYear) || 0;
        const californiaUnits = californiaByYear.get(latestYear) || 0;
        const locationPopulation = locationPopMap.get(latestYear) || 0;
        const californiaPopulation = californiaPopMap.get(latestYear) || 0;

        const locationPer10000 = locationPopulation > 0 ? (locationUnits / locationPopulation) * 10000 : 0;
        const californiaPer10000 = californiaPopulation > 0 ? (californiaUnits / californiaPopulation) * 10000 : 0;
        onDataLoaded?.([
          { year: latestYear, location, value: locationPer10000 },
          { year: latestYear, location: 'California', value: californiaPer10000 }
        ]);
        const createChart = (attempt = 0) => {
          if (!chartRef.current) {
            if (attempt < 3) {
              setTimeout(() => createChart(attempt + 1), 100);
            }
            return;
          }

          const ctx = chartRef.current.getContext('2d');
          if (!ctx) {
            return;
          }

          // Destroy existing chart if it exists
          if (chartInstance.current) {
            chartInstance.current.destroy();
          }

          chartInstance.current = new Chart(ctx, {
              type: 'bar',
              data: {
                labels: [location, 'California Average'],
                datasets: [{
                  label: `Heat Pumps per 10,000 Residents (${latestYear})`,
                  data: [locationPer10000, californiaPer10000],
                  backgroundColor: ['#3b82f6', '#10b981'],
                  borderColor: ['#2563eb', '#059669'],
                  borderWidth: 2
                }]
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  title: standardTitle(`Heat Pumps per 10,000 Residents - ${location} vs California (${latestYear})`),
                  legend: standardLegend,
                  tooltip: {
                    callbacks: {
                      label: (context: any) => {
                        const value = Number(context.parsed?.y ?? 0);
                        return `${value.toFixed(2)} heat pumps per 10,000 residents`;
                      }
                    }
                  }
                },
                scales: {
                  x: {
                    title: {
                      display: true,
                      text: 'Location',
                      font: { size: 14, weight: 'bold' }
                    }
                  },
                  y: {
                    title: {
                      display: true,
                      text: 'Heat Pumps per 10,000 Residents',
                      font: { size: 14, weight: 'bold' }
                    },
                    beginAtZero: true,
                    ticks: {
                      callback: (value: any) => `${Number(value).toFixed(2)}`
                    }
                  }
                }
              }
          });
        };

        createChart();

      } catch (err) {
        console.error('Error fetching heat pump per capita data:', err);
        const message = err instanceof Error ? err.message : formatSupabaseError(err);
        console.error('HeatPumpPerCapitaChart Supabase error:', message);
        setError(`Failed to load heat pump per capita data: ${message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchPerCapitaData();

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [location, geofips]);

  if (loading) {
    return <div>Loading heat pump per capita comparison data...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div style={{ height: '380px', width: '100%' }}>
      <canvas ref={chartRef} />
    </div>
  );
};


